import api, { API } from './api';
import { logDebug } from './devlog';

const CHUNK_SIZE_DEFAULT = 5 * 1024 * 1024; // 5MB chunks

/**
 * Chunked upload helper.
 * @param {File} file
 * @param {object} meta - { title, source_type }
 * @param {function} onProgress - called with (percentComplete, stageLabel)
 * @returns {Promise<{video_id: string}>}
 */
export async function chunkedUpload(file, meta, onProgress) {
  const chunkSize = CHUNK_SIZE_DEFAULT;
  const totalChunks = Math.ceil(file.size / chunkSize);

  // 1) Init upload session
  const { data: initData } = await api.post('/uploads/init', {
    filename: file.name,
    size: file.size,
    total_chunks: totalChunks,
    title: meta.title,
    source_type: meta.source_type,
  });
  const uploadId = initData.upload_id;

  // 2) Upload chunks sequentially
  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    const blob = file.slice(start, end);
    const fd = new FormData();
    fd.append('file', blob, `chunk_${i}`);

    let attempt = 0;
    while (true) {
      try {
        await api.post(`/uploads/${uploadId}/chunk/${i}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        break;
      } catch (err) {
        attempt++;
        if (attempt >= 3) throw err;
        await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }

    const pct = Math.round(((i + 1) / totalChunks) * 100);
    onProgress?.(pct, `Upload ${i + 1}/${totalChunks}`);
  }

  // 3) Complete
  onProgress?.(100, 'Assembling on server');
  const { data: completeData } = await api.post(`/uploads/${uploadId}/complete`, {});
  return { video_id: completeData.video_id };
}

/**
 * Open a WebSocket to follow video processing progress.
 * Returns { close } and dispatches messages to handler.
 */
export function openVideoWS(videoId, onMessage) {
  // Derive ws:// or wss:// from API URL
  const httpUrl = new URL(API);
  const wsProto = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${wsProto}//${httpUrl.host}/api/ws/videos/${videoId}`;
  const ws = new WebSocket(wsUrl);
  ws.onmessage = (evt) => {
    try {
      const data = JSON.parse(evt.data);
      onMessage?.(data);
    } catch (e) {
      // Malformed WS payload — ignore (server-side reconnect handles it)
      logDebug('WS message parse failed', e);
    }
  };
  ws.onerror = (e) => { logDebug('WS error', e); };
  return { ws, close: () => ws.close() };
}
